/**
 * MyAgent System Prompts
 *
 * Default system prompts and prompt configurations for MyAgent
 */

export const MYAGENT_BOT_ID = '__myagent__'

// Tool categories defined here — myagent_core owns these (matches getTools allCategories)
export const ALL_TOOL_CATEGORIES = ['notes', 'web', 'notification']

/**
 * Default myagent bot config (built-in, not stored in user data)
 */
export const MYAGENT_DEFAULT_CONFIG = {
  botId: MYAGENT_BOT_ID,
  displayName: 'MyAgent',
  description: 'Your AI assistant',
  llm: {
    mode: 'remote',
    provider: null,
    model: null,
    apiKey: null,
    workerPublicKey: null,
  },
  role: {
    systemPrompt: null,
  },
  toolCategories: ALL_TOOL_CATEGORIES,
  isBuiltIn: true,
}

/**
 * Build the default MyAgent role description
 */
function defaultRolePrompt() {
  return `You are MyAgent, the host bot in a group chat. You are the user's primary AI assistant.

Rules:
- Present yourself as MyAgent, not as any specific AI model
- Respond in the user's language
- Use tools when needed to complete tasks
- The user talks to you by default (no @mention needed) or via @MyAgent
- Other bots in the chat can be reached via @mention — you do NOT need to relay messages for them`
}

/**
 * Build tool usage guide based on enabled tool categories
 * @param {string[]} toolCategories - Enabled category IDs
 * @returns {string}
 */
function buildToolGuide(toolCategories) {
  const enabled = new Set(toolCategories || ALL_TOOL_CATEGORIES)
  const localTools = []
  if (enabled.has('notes')) localTools.push('notes')
  if (enabled.has('notification')) localTools.push('notification')
  if (enabled.has('web')) localTools.push('web search')

  let guide = `You have three tools:
1. executeCode — Run JavaScript code for LOCAL tools only (${localTools.join(', ') || 'none enabled'})
2. talkToRole — Call a REMOTE role directly (no code needed)
3. searchRoleCapabilities — Search a remote role's tools by query

IMPORTANT: Tool roles (listed below) can ONLY be accessed via talkToRole. Do NOT use executeCode or searchTools for remote roles — they are not in the local tool registry.

Guidelines:
- LOCAL tools (${localTools.join(', ') || 'none'}): use executeCode
- TOOL roles: use talkToRole — always check the role list below first
- CHAT bots: the user @mentions them directly, no action needed from you
- Use searchRoleCapabilities when unsure which toolPath to use for a tool role
- toolPaths use the role's OWN namespace (e.g. 'tabs/createTab')`

  return guide
}

/**
 * Build roles section for system prompt
 * @param {Array} roles - Available remote roles
 * @returns {string}
 */
function buildRolesSection(roles) {
  const externalRoles = (roles || []).filter(r => r.roleType !== 'host')
  if (externalRoles.length === 0) return ''

  const chatRoles = externalRoles.filter(r => r.roleType === 'chat')
  const toolRoles = externalRoles.filter(r => r.roleType !== 'chat')
  let section = ''

  if (chatRoles.length > 0) {
    section += '\n\n## Chat Bots (user can @mention these directly)\nThese bots support direct conversation. The user may @mention them and the response will appear directly. You do NOT need to call talkToRole for these unless the user explicitly asks you to.\n'
    for (const role of chatRoles) {
      section += `\n- ${role.displayName} ID: ${role.roleId}`
      section += `\n  ${role.description}\n`
    }
  }

  if (toolRoles.length > 0) {
    section += '\n\n## Tool Roles (use talkToRole to interact)\n'
    for (const role of toolRoles) {
      section += `\n- ${role.displayName} ID: ${role.roleId}`
      section += `\n  ${role.description}`
      section += `\n  → talkToRole({ roleId: '${role.roleId}', toolPath: 'category/tool', toolArgs: '{"key":"value"}' })\n`
    }
  }

  return section
}

/**
 * Build system prompt for a specific bot config
 * @param {Object} botConfig - Bot configuration (from bot-config.js)
 * @param {Array} roles - Available remote roles
 * @returns {string}
 */
export function buildBotSystemPrompt(botConfig, roles = []) {
  const roleDesc = botConfig?.role?.systemPrompt || defaultRolePrompt()
  const toolGuide = buildToolGuide(botConfig?.toolCategories)
  const rolesSection = buildRolesSection(roles)
  return `${roleDesc}\n\n${toolGuide}${rolesSection}`
}

